import type { JSX } from 'react';
import { useId, useState } from 'react';

import { Button, VisuallyHidden } from '@trabeate/design-system';

import { WAITLIST_COPY } from '../config/content';
import { submitWaitlist } from '../lib/waitlist-client';

import styles from './WaitlistForm.module.css';

/** The four states REQ-WAITLIST-002 requires, named once so no call site can spell one twice. */
const STATUS = {
  IDLE: 'idle',
  SUBMITTING: 'submitting',
  JOINED: 'joined',
  FAILED: 'failed',
} as const;

/** Exactly one of these at any moment. */
type Status =
  | { kind: typeof STATUS.IDLE }
  | { kind: typeof STATUS.SUBMITTING }
  | { kind: typeof STATUS.JOINED }
  | { kind: typeof STATUS.FAILED; message: string };

/**
 * The one action on the page.
 *
 * Client-side validation here is a courtesy that saves a round trip, never a control. The Lambda
 * re-validates every submission and is the only thing that decides what is stored — anything
 * enforced only in this component is enforced nowhere, because a client is untrusted.
 *
 * Note what this component deliberately cannot do: tell a first signup from a repeat one. The
 * backend writes unconditionally and answers both identically, so that this public,
 * unauthenticated surface cannot be used to test whether a given person is on the waitlist
 * (REQ-WAITLIST-003). If a "you're already on the list" message ever seems worth adding, that
 * is the reason it is not there.
 */
export function WaitlistForm(): JSX.Element {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>({ kind: STATUS.IDLE });
  const fieldId = useId();
  const messageId = useId();

  // `React.FormEvent` is deprecated in @types/react 19 — "FormEvent doesn't actually exist",
  // it was always an alias. SyntheticEvent is what a submit handler actually receives, and
  // preventDefault is all this needs from it.
  async function onSubmit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();

    // The guard against a double submit. Repeated clicks and repeated Enter presses both land
    // here, and both are ignored while a request is in flight (REQ-WAITLIST-005).
    if (status.kind === STATUS.SUBMITTING) {
      return;
    }

    setStatus({ kind: STATUS.SUBMITTING });
    const result = await submitWaitlist(email);

    if (result.ok) {
      setStatus({ kind: STATUS.JOINED });
      setEmail('');
    } else {
      // The address stays in the field. Making someone retype it to retry is a good way to
      // lose them at the last step.
      setStatus({ kind: STATUS.FAILED, message: result.message });
    }
  }

  // Replacing the form outright rather than showing a message beside it: leaving a filled-in
  // form on screen after a successful signup invites a second submit and reads as failure.
  if (status.kind === STATUS.JOINED) {
    return (
      <div className={styles.frame}>
        <p className={styles.confirmation} role="status">
          {WAITLIST_COPY.confirmation}
        </p>
      </div>
    );
  }

  // Both narrow `status` for the rest of the render — TypeScript follows a discriminant
  // through a const alias — and both keep the condition out of the JSX below.
  const failed = status.kind === STATUS.FAILED;
  const submitting = status.kind === STATUS.SUBMITTING;
  const buttonLabel = submitting ? WAITLIST_COPY.submitting : WAITLIST_COPY.submit;
  const message = failed ? status.message : '';

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
    >
      <div className={styles.frame}>
        <VisuallyHidden>
          <label htmlFor={fieldId}>{WAITLIST_COPY.fieldLabel}</label>
        </VisuallyHidden>

        <input
          id={fieldId}
          className={styles.field}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={WAITLIST_COPY.placeholder}
          value={email}
          onChange={(event) => { setEmail(event.target.value); }}
          // Conveys the rejected state to assistive technology, so a screen-reader user gets
          // the same signal a sighted one gets from the message.
          aria-invalid={failed}
          aria-describedby={messageId}
          required
        />

        {/* `lg` is 48px tall, above REQ-LANDING-003's 44px minimum. */}
        <Button type="submit" variant="primary" size="lg" disabled={submitting}>
          {buttonLabel}
        </Button>
      </div>

      {/* `role="status"` announces the message when it appears without moving focus. The node is
          always present so assistive technology is watching it before there is anything to
          say — a live region added to the DOM at the same moment as its content is frequently
          not announced at all. */}
      <p id={messageId} className={styles.message} role="status">
        {message}
      </p>

      <p className={styles.finePrint}>{WAITLIST_COPY.finePrint}</p>
    </form>
  );
}
